// client-switch.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { Library } from '../library';

export interface ClientInfo {
  id: string;
  name: string;
  value: string;
}

export interface PreviousAccount {
  userId: number;
  clientId: string;
  bPartnerId: number;
  name: string;
  isActingAsClient: boolean;
  serviceProviderId: number;
}

export interface ClientSwitchRequest {
  clientId: string;
}

export interface TokenResponse {
  token: string;
  previousAccount?: PreviousAccount;
}

@Injectable({
  providedIn: 'root'
})
export class ClientSwitchService {
  private apiUrl = Library.API_ENDPOINT + 'auth';
  private currentClientSubject = new BehaviorSubject<ClientInfo | null>(null);
  public currentClient$ = this.currentClientSubject.asObservable();

  private previousAccountSubject = new BehaviorSubject<PreviousAccount | null>(null);
  public previousAccount$ = this.previousAccountSubject.asObservable();

  constructor(private http: HttpClient) {
    // Load current client from localStorage on service initialization
    const savedClient = localStorage.getItem('currentClient');
    if (savedClient) {
      try {
        this.currentClientSubject.next(JSON.parse(savedClient));
      } catch (e) {
        console.error('Error parsing saved client:', e);
      }
    }

    // Load previous account from localStorage
    const savedPreviousAccount = localStorage.getItem('previousAccount');
    if (savedPreviousAccount) {
      try {
        this.previousAccountSubject.next(JSON.parse(savedPreviousAccount));
      } catch (e) {
        console.error('Error parsing saved previous account:', e);
      }
    }
  }

  /**
   * Get list of available clients for service provider
   */
  getAvailableClients(): Observable<{ clients: ClientInfo[] }> {
    const headers = this.getAuthHeaders();
    return this.http.get<{ clients: ClientInfo[] }>(
      `${this.apiUrl}/get-clients`,
      { headers }
    ).pipe(
      catchError(error => {
        console.error('Error fetching clients:', error);
        return of({ clients: [] });
      })
    );
  }

  /**
   * Switch to a different client account
   */
  switchClient(clientId: string, bPartnerId?: number): Observable<TokenResponse> {
    const headers = this.getAuthHeaders();
    const request: any = { clientId };

    // Include bPartnerId if provided (for going back to specific account)
    if (bPartnerId !== undefined && bPartnerId !== null) {
      request.bPartnerId = bPartnerId;
    }

    return this.http.post<TokenResponse>(
      `${this.apiUrl}/switch-client`,
      request,
      { headers }
    ).pipe(
      tap(response => {
        if (response && response.token) {
          // Store new token
          localStorage.setItem('jwt', response.token);
          localStorage.setItem('selectedClient', clientId);

          // Store previous account info for "go back" functionality
          if (response.previousAccount) {
            localStorage.setItem('previousAccount', JSON.stringify(response.previousAccount));
            this.previousAccountSubject.next(response.previousAccount);
          }

          // Decode and store user info from token
          this.decodeAndStoreUserInfo(response.token);

          // Update current client
          this.updateCurrentClientInfo(clientId);
        }
      }),
      catchError(error => {
        console.error('Error switching client:', error);
        throw error;
      })
    );
  }

  /**
   * Go back to previous account
   */
  goBackToPreviousAccount(): Observable<TokenResponse> {
    const previousAccount = this.previousAccountSubject.value;

    if (!previousAccount) {
      console.error('No previous account found');
      return of(null);
    }

    // Pass both clientId and bPartnerId from previous account
    return this.switchClient(previousAccount.clientId, previousAccount.bPartnerId).pipe(
      tap(() => {
        // Clear previous account after switching back
        localStorage.removeItem('previousAccount');
        this.previousAccountSubject.next(null);
      })
    );
  }

  /**
   * Check if there's a previous account to go back to
   */
  hasPreviousAccount(): boolean {
    return this.previousAccountSubject.value !== null;
  }

  /**
   * Get previous account information
   */
  getPreviousAccount(): PreviousAccount | null {
    return this.previousAccountSubject.value;
  }

  /**
   * Update current client information
   */
  private updateCurrentClientInfo(clientId: string): void {
    this.getAvailableClients().subscribe(response => {
      const client = response.clients.find(c => c.id === clientId);
      if (client) {
        this.currentClientSubject.next(client);
        localStorage.setItem('currentClient', JSON.stringify(client));
      }
    });
  }

  /**
   * Get current client
   */
  getCurrentClient(): ClientInfo | null {
    return this.currentClientSubject.value;
  }

  /**
   * Clear client selection
   */
  clearClientSelection(): void {
    this.currentClientSubject.next(null);
    localStorage.removeItem('selectedClient');
    localStorage.removeItem('currentClient');
    localStorage.removeItem('previousAccount');
    this.previousAccountSubject.next(null);
  }

  /**
   * Get authorization headers
   */
  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('jwt');
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });
  }

  /**
   * Decode JWT token and store user info
   */
  private decodeAndStoreUserInfo(token: string): void {
    try {
      const payload = this.decodeJWT(token);

      // Update account info with client context
      const accountInfo = JSON.parse(localStorage.getItem('account') || '{}');

      accountInfo.userId = payload.userId;
      accountInfo.name = payload.name;
      accountInfo.bPartnerId = payload.bPartnerId;
      accountInfo.actingAsClient = payload.actingAsClient || false;
      accountInfo.serviceProviderId = payload.serviceProviderId;
      accountInfo.clientId = payload.clientId;

      localStorage.setItem('account', JSON.stringify(accountInfo));

    } catch (error) {
      console.error('Error decoding token:', error);
    }
  }

  /**
   * Decode JWT token
   */
  private decodeJWT(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64).split('').map(c => {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding JWT:', error);
      return {};
    }
  }

  /**
   * Check if user is currently acting as a client
   */
  isActingAsClient(): boolean {
    const accountInfo = JSON.parse(localStorage.getItem('account') || '{}');
    return accountInfo.actingAsClient === true;
  }

  /**
   * Get service provider ID if acting as client
   */
  getServiceProviderId(): number | null {
    const accountInfo = JSON.parse(localStorage.getItem('account') || '{}');
    return accountInfo.serviceProviderId || null;
  }
}